/**
 * Client deck — prototype `VIEWS.deck` (docs/prototype.html L4356–4377), opened by "Make client deck" on a client.
 * Route: /clients/deck?client=<id>&deck=<id>. The deck id in the address keeps the preview across a reload.
 *
 *   Set up    Client (managers: every client; a member: the clients they are SPOC of), Period (this month, this
 *             quarter), Sections (Highlights, Usage, What we delivered, Next month), Generate deck.
 *   Preview   "Nothing generated yet" → "Putting the deck together" → the slides as generated, then Download PDF and
 *             Download PowerPoint.
 *
 * Every number comes from the stored deck (POST /clients/{id}/decks, GET /client-decks/{id}); the downloads are the
 * same snapshot. Revenue never appears: the contract has no money field.
 * Changing the client, the period or a section clears the preview, since the downloads are of the generated deck.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { DECK_SECTIONS, DECK_SECTION_LABEL, deckPeriodLabel, type ClientDeck as Deck, type DeckFormat, type DeckSection, type DeckSlide } from '@amplispot/contracts';
import { useSession } from '../../lib/session';
import { useClients } from '../../lib/data';
import { deckPeriods, downloadDeck, useCreateDeck, useDeck } from '../../lib/decks';
import { ErrorState, Loading, errorText, useToast } from '../../ui/kit';
import { PageInfo } from '../../shell/Help';

export function ClientDeck() {
  const { can, me, readOnly } = useSession();
  const toast = useToast();
  const [q, setQ] = useSearchParams();
  const clients = useClients();
  const create = useCreateDeck();
  const offered = useMemo(() => deckPeriods(), []);
  const [period, setPeriod] = useState(offered[0]!);
  const [sec, setSec] = useState<Record<DeckSection, boolean>>({ highlights: true, usage: true, delivered: true, next_month: true });

  // Managers make a deck for any client; a member for the clients they are the current SPOC of (the server agrees).
  const mine = useMemo(
    () => (clients.data ?? []).filter((c) => can('clients.read.all') || c.spoc?.id === me.user.id),
    [clients.data, can, me.user.id],
  );
  const clientId = q.get('client') && mine.some((c) => c.id === q.get('client')) ? q.get('client')! : mine[0]?.id ?? null;
  const deckId = q.get('deck');
  const deck = useDeck(deckId);
  const shown = deck.data && deck.data.client.id === clientId ? deck.data : null;
  // A deck opened from the address shows its own period and sections in Set up (a period not offered is added to the list).
  const periods = shown && !offered.includes(shown.period) ? [...offered, shown.period] : offered;
  useEffect(() => {
    if (!shown) return;
    setPeriod(shown.period);
    setSec({ highlights: false, usage: false, delivered: false, next_month: false, ...Object.fromEntries(shown.sections.map((s) => [s, true])) });
    // Only when a different deck arrives; editing Set up afterwards clears the preview.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown?.id]);

  const set = (patch: Record<string, string | null>) =>
    setQ(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch)) (v == null ? n.delete(k) : n.set(k, v));
        return n;
      },
      { replace: true },
    );
  const clear = () => {
    create.reset();
    set({ deck: null });
  };

  const generate = () => {
    if (!clientId) return;
    const sections = DECK_SECTIONS.filter((s) => sec[s]);
    if (sections.length === 0) return toast('Pick at least one section');
    create.mutate({ clientId, body: { period, sections } }, { onSuccess: (d) => set({ client: d.client.id, deck: d.id }) });
  };

  const preview: ReactNode = create.isPending ? (
    <Generating />
  ) : create.isError ? (
    <ErrorState error={create.error} onRetry={generate} />
  ) : deckId && deck.isPending ? (
    <Loading label="Loading the deck…" />
  ) : deckId && deck.isError ? (
    <ErrorState error={deck.error} onRetry={() => deck.refetch()} />
  ) : shown ? (
    <Slides d={shown} />
  ) : (
    <div className="empty">
      <b>Nothing generated yet</b>Pick a client and the sections, then generate.
    </div>
  );

  return (
    <>
      <div className="ph">
        <div>
          <h1>
            Client deck
            <PageInfo helpKey="deck" />
          </h1>
          <p>Built from what is already in Ops: delivered tasks, usage, meetings and client tasks. Revenue never appears in a client deck.</p>
        </div>
      </div>
      <div className="cols c12">
        <div className="panel" style={{ alignSelf: 'start' }}>
          <div className="hd">
            <h3>Set up</h3>
          </div>
          <div className="bd">
            {clients.isPending ? (
              <Loading label="Loading your clients…" />
            ) : clients.isError ? (
              <ErrorState error={clients.error} onRetry={() => clients.refetch()} />
            ) : mine.length === 0 ? (
              <div className="empty">
                <b>No clients yet</b>A deck is made for a client you look after.
              </div>
            ) : (
              <>
                <label className="fld">
                  <span>Client</span>
                  <select
                    className="in"
                    value={clientId ?? ''}
                    onChange={(e) => {
                      create.reset();
                      set({ client: e.target.value, deck: null });
                    }}
                  >
                    {mine.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="fld">
                  <span>Period</span>
                  <select
                    className="in"
                    value={period}
                    onChange={(e) => {
                      setPeriod(e.target.value);
                      clear();
                    }}
                  >
                    {periods.map((p) => (
                      <option key={p} value={p}>
                        {deckPeriodLabel(p)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="lbl" style={{ margin: '6px 0 8px' }}>
                  Sections
                </div>
                {DECK_SECTIONS.map((s) => (
                  <label key={s} className="split" style={{ padding: '6px 0' }}>
                    <input
                      type="checkbox"
                      checked={sec[s]}
                      onChange={(e) => {
                        setSec((m) => ({ ...m, [s]: e.target.checked }));
                        clear();
                      }}
                    />
                    {DECK_SECTION_LABEL[s]}
                  </label>
                ))}
                <button
                  className="btn pri"
                  style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}
                  onClick={generate}
                  disabled={create.isPending || readOnly}
                  title={readOnly ? 'Not while viewing as someone' : undefined}
                >
                  {create.isPending ? 'Generating…' : 'Generate deck'}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="panel">
          <div className="hd">
            <h3>Preview</h3>
          </div>
          <div className="bd">{preview}</div>
        </div>
      </div>
    </>
  );
}

/** Prototype: "Putting the deck together" over a filling meter. The request builds the deck in one go. */
function Generating() {
  const [w, setW] = useState(0);
  useEffect(() => {
    const t = window.setTimeout(() => setW(90), 30);
    return () => window.clearTimeout(t);
  }, []);
  return (
    <div className="empty" role="status">
      <b>Putting the deck together</b>
      <div className="meter" style={{ maxWidth: 240, margin: '12px auto 0' }}>
        <i style={{ width: `${w}%`, transition: 'width 2.5s ease-out' }} />
      </div>
    </div>
  );
}

function Slide({ n, children }: { n: string; children: ReactNode }) {
  return (
    <div
      style={{
        aspectRatio: '16/9',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: '18px 22px',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="lbl" style={{ marginBottom: 10 }}>
        {n}
      </div>
      {children}
      <div style={{ position: 'absolute', right: 14, bottom: 10, fontSize: 10, color: 'var(--mute)' }}>Amplispot</div>
    </div>
  );
}

const pct = (n: number | null) => (n == null ? 'n/a' : `${n}%`);

function SlideBody({ s }: { s: DeckSlide }) {
  switch (s.kind) {
    case 'cover':
      return (
        <Slide n="Cover">
          <div style={{ margin: 'auto 0' }}>
            <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.01em' }}>{s.client_name}</div>
            <div style={{ color: 'var(--sub)', marginTop: 4 }}>{s.subtitle}</div>
          </div>
        </Slide>
      );
    case 'highlights':
      return (
        <Slide n={DECK_SECTION_LABEL.highlights}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, margin: 'auto 0' }}>
            {(
              [
                ['Tasks delivered', String(s.tasks_delivered)],
                ['Requests closed', String(s.requests_closed)],
                ['Tasks on time', pct(s.on_time_pct)],
              ] as const
            ).map(([l, v]) => (
              <div key={l}>
                <div className="mono" style={{ fontSize: 26, fontWeight: 600 }}>
                  {v}
                </div>
                <div style={{ color: 'var(--sub)', fontSize: 12 }}>{l}</div>
              </div>
            ))}
          </div>
        </Slide>
      );
    case 'usage':
      return (
        <Slide n={DECK_SECTION_LABEL.usage}>
          <div style={{ display: 'grid', gap: 10, margin: 'auto 0' }}>
            {s.products.length === 0 ? <div style={{ fontSize: 13, color: 'var(--sub)' }}>No active products</div> : null}
            {s.products.map((p) => (
              <div key={p.product_key} className="split">
                <div style={{ width: 140, fontSize: 13, fontWeight: 600 }}>{p.product_name}</div>
                <div className="meter grow">
                  <i style={{ width: `${Math.min(100, p.util ?? 0)}%` }} />
                </div>
                <span className="mono" style={{ width: 44, textAlign: 'right' }}>
                  {pct(p.util)}
                </span>
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--sub)' }}>{s.caption}</div>
          </div>
        </Slide>
      );
    default:
      return (
        <Slide n={DECK_SECTION_LABEL[s.kind]}>
          <ul style={{ margin: '0 0 0 16px', fontSize: 13, display: 'grid', gap: 5 }}>
            {s.items.map((t) => (
              <li key={t.task_id}>{t.title}</li>
            ))}
            {s.items.length === 0 ? <li>{s.kind === 'delivered' ? 'Nothing in this period' : 'No open items'}</li> : null}
            {s.more > 0 ? <li style={{ color: 'var(--sub)' }}>and {s.more} more</li> : null}
          </ul>
        </Slide>
      );
  }
}

function Slides({ d }: { d: Deck }) {
  const toast = useToast();
  const [busy, setBusy] = useState<DeckFormat | null>(null);
  const get = async (f: DeckFormat) => {
    setBusy(f);
    try {
      await downloadDeck(d.id, f);
    } catch (e) {
      toast(errorText(e));
    } finally {
      setBusy(null);
    }
  };
  return (
    <>
      <div style={{ display: 'grid', gap: 14 }}>
        {d.slides.map((s, i) => (
          <SlideBody key={`${s.kind}-${i}`} s={s} />
        ))}
      </div>
      <div className="split" style={{ marginTop: 14 }}>
        <button className="btn" onClick={() => void get('pdf')} disabled={busy !== null}>
          {busy === 'pdf' ? 'Preparing the PDF…' : 'Download PDF'}
        </button>
        <button className="btn pri" onClick={() => void get('pptx')} disabled={busy !== null}>
          {busy === 'pptx' ? 'Preparing the PowerPoint…' : 'Download PowerPoint'}
        </button>
      </div>
    </>
  );
}
